import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BellRing, ChevronDown, Lock, RotateCcw } from 'lucide-react';
import { api } from '../services/api';
import PendingDecision from './ui/PendingDecision';
import type {
  NotificationChannel,
  NotificationPreferenceEvent,
  NotificationPreferenceUpdate,
} from '../types/clientWorkspace';

// ============================================================================
// Notification preferences (decisions 96/97): per event type and channel
// (in-app / email / push), plus a channel-wide default. Mandatory channels of
// critical events are shown locked — the server ignores opt-outs for them.
// ============================================================================

const ALL_CHANNELS: NotificationChannel[] = ['in_app', 'email', 'push'];

function parseApiError(err: unknown): string | null {
  if (err && typeof err === 'object' && 'error' in err) {
    const e = (err as { error?: { message?: string } }).error;
    if (e?.message) return e.message;
  }
  return null;
}

function Toggle({ checked, disabled, onChange, label }: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 ${checked ? 'bg-primary-500' : 'bg-surface-300'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-on-accent transition-transform ${checked ? 'translate-x-0.5 rtl:-translate-x-0.5' : 'translate-x-[18px] rtl:-translate-x-[18px]'}`} />
    </button>
  );
}

/**
 * `audience="portal"` (Q-DQ-114): a customer's "הפרופיל שלי" - only the types a customer receives.
 * Staff (the default) see everything, as before.
 */
export default function NotificationPreferences({ audience }: { audience?: 'portal' } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.getNotificationPreferences(),
  });
  const prefs = data?.data;

  // DQ-239: no VAPID key configured on the server = push can never work, so the "push"
  // column is hidden (until the answer is known, to avoid a flash of a dead switch).
  const { data: vapidData } = useQuery({
    queryKey: ['push-vapid-key'],
    queryFn: () => api.getVapidPublicKey(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const pushAvailable = !!vapidData?.data?.publicKey;
  const CHANNELS = useMemo(
    () => ALL_CHANNELS.filter((ch) => ch !== 'push' || pushAvailable),
    [pushAvailable],
  );

  const mutation = useMutation({
    mutationFn: (updates: NotificationPreferenceUpdate[]) => api.updateNotificationPreferences(updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-preferences'] }),
  });

  const groups = useMemo(() => {
    const map = new Map<string, NotificationPreferenceEvent[]>();
    for (const e of prefs?.events || []) {
      if (audience === 'portal' && e.portal_audience !== true) continue;
      if (!map.has(e.group)) map.set(e.group, []);
      map.get(e.group)!.push(e);
    }
    return [...map.entries()];
  }, [prefs, audience]);

  if (isLoading) return <div className="card text-sm text-surface-500">{t('notificationPrefs.loading')}</div>;
  if (error || !prefs) {
    return <div className="card text-sm text-danger-700">{parseApiError(error) || t('notificationPrefs.loadError')}</div>;
  }

  const effective = (e: NotificationPreferenceEvent, ch: NotificationChannel): boolean =>
    e.settings[ch] ?? prefs.defaults[ch];

  const channelLabel = (ch: NotificationChannel) => t(`notificationPrefs.channel.${ch}`);

  return (
    <div className="card space-y-4" id="notifications">
      <div className="flex items-center gap-3">
        <BellRing className="text-accent-text" size={24} />
        <div>
          <h2 className="text-lg font-semibold text-surface-900">{t('notificationPrefs.title')}</h2>
          <p className="text-sm text-surface-500">{t('notificationPrefs.subtitle')}</p>
        </div>
      </div>

      {mutation.isError && (
        <div className="text-sm text-danger-700 bg-danger-50 rounded-lg px-3 py-2">
          {parseApiError(mutation.error) || t('notificationPrefs.saveError')}
        </div>
      )}

      {/* Channel-wide defaults ('*') */}
      <div className="rounded-lg border border-surface-200 p-3">
        <p className="text-xs font-semibold text-surface-500 mb-2">{t('notificationPrefs.defaults')}</p>
        <div className="flex flex-wrap gap-4">
          {CHANNELS.map((ch) => (
            <label key={ch} className="flex items-center gap-2 text-sm text-surface-700">
              <Toggle
                checked={prefs.defaults[ch]}
                disabled={mutation.isPending}
                label={channelLabel(ch)}
                onChange={() => mutation.mutate([{ event_type: '*', channel: ch, enabled: !prefs.defaults[ch] }])}
              />
              {channelLabel(ch)}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {groups.map(([group, events]) => {
          const isOpen = openGroups[group] ?? false;
          return (
            <div key={group} className="rounded-lg border border-surface-200">
              <button
                type="button"
                onClick={() => setOpenGroups((g) => ({ ...g, [group]: !isOpen }))}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-surface-800"
                aria-expanded={isOpen}
              >
                <span>{t(`notificationPrefs.group.${group}`)} ({events.length})</span>
                <ChevronDown size={16} className={`transition-transform ${isOpen ? '' : '-rotate-90 rtl:rotate-90'}`} />
              </button>
              {isOpen && (
                <div className="overflow-x-auto border-t border-surface-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-surface-500">
                        <th className="text-start font-medium px-3 py-2">{t('notificationPrefs.event')}</th>
                        {CHANNELS.map((ch) => (
                          <th key={ch} className="font-medium px-2 py-2 text-center">{channelLabel(ch)}</th>
                        ))}
                        <th className="px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e) => {
                        const hasOverride = CHANNELS.some((ch) => e.settings[ch] !== null && !e.mandatory.includes(ch));
                        return (
                          <tr key={e.event_type} className="border-t border-surface-100">
                            <td className="px-3 py-2 text-surface-800">
                              {t(`notifications.${e.event_type}`, { defaultValue: e.event_type })}
                              {/* P-32a (D-184, PM-21) */}
                              {e.event_type === 'hour_bank_exhausted' && <PendingDecision id="PM-21" className="ms-1" />}
                              {e.mandatory.length > 0 && (
                                <span className="ms-2 inline-flex items-center gap-1 text-xs text-warning-700 bg-warning-50 rounded-xs px-1.5 py-0.5">
                                  <Lock size={12} /> {t('notificationPrefs.mandatory')}
                                </span>
                              )}
                            </td>
                            {CHANNELS.map((ch) => {
                              const locked = e.mandatory.includes(ch);
                              const on = locked || effective(e, ch);
                              // DQ-231: a type that never sends e-mail has no e-mail switch.
                              if (ch === 'email' && e.email_capable === false) {
                                return (
                                  <td key={ch} className="px-2 py-2 text-center text-surface-400" title={t('notificationPrefs.noEmailForType')}>
                                    <span aria-label={t('notificationPrefs.noEmailForType')}>—</span>
                                  </td>
                                );
                              }
                              return (
                                <td key={ch} className="px-2 py-2 text-center">
                                  <span title={locked ? t('notificationPrefs.mandatoryHint') : undefined} className="inline-flex">
                                    <Toggle
                                      checked={on}
                                      disabled={locked || mutation.isPending}
                                      label={`${t(`notifications.${e.event_type}`, { defaultValue: e.event_type })} — ${channelLabel(ch)}`}
                                      onChange={() => mutation.mutate([{ event_type: e.event_type, channel: ch, enabled: !on }])}
                                    />
                                  </span>
                                </td>
                              );
                            })}
                            <td className="px-2 py-2 text-center">
                              {hasOverride && (
                                <button
                                  type="button"
                                  onClick={() => mutation.mutate(
                                    CHANNELS.filter((ch) => !e.mandatory.includes(ch)).map((ch) => ({ event_type: e.event_type, channel: ch, enabled: null }))
                                  )}
                                  className="p-1 rounded-xs text-surface-500 hover:text-surface-700 hover:bg-surface-100"
                                  aria-label={t('notificationPrefs.reset')}
                                  title={t('notificationPrefs.reset')}
                                >
                                  <RotateCcw size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
